import React, { useContext, useState } from 'react';
import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { Visibility, VisibilityOff, VpnKey } from '@material-ui/icons';

import { useHistory } from 'react-router-dom';

import { Formik } from 'formik';

import PropTypes from 'prop-types';

import BasicButton from '../../buttons/basicButton';
import SpinnerLoading from '../../feedback/spinnerLoading';

import { newClinician, updateClinician } from '../../../utils/comms';

import { newClinicianDetailsVS, updateClinicianDetailsVS } from '../../../utils/formValidations';

import translations from '../../../languages/i18n';

import { SessionContext } from '../../../utils/context';

export default function ClinicianDetails({ type, detailsData, handleFeedback, handleShowChangePassword }) {
  const sessionContext = useContext(SessionContext);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const history = useHistory();

  const goBack = () => {
    history.goBack();
  };

  const onChangePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onChangeConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Per interrompre la 'bubble' i evitar que el navegador executi el seu comportament habitual davant d'aquest event
  const onMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const updateData = async (values, resetForm) => {
    setShowLoading(true);
    let response = null;
    const clinician = {
      username: values.username,
      password: values.password,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
    };
    if (type === 'new') {
      response = await newClinician({ clinician }, sessionContext.userToken);
    } else {
      // setSentValues(values); // En cas de fer un edit, en refrescar la pantalla es mostrarà el formulari amb les dades enviades
      response = await updateClinician({ clinician }, detailsData._id, sessionContext.userToken);
    }
    setShowLoading(false);
    if (response.status === 200) { // Programa creat/modificat
      if (type === 'new') resetForm(); // En cas de crear un nou clinic, en mostrar la pantalla es mostrarà el formulari buit
      setShowConfirmPassword(false); // Ho tornam al valor inicial, per si l'usuari ho havia deixat en mode mostrar contrasenya
      setShowPassword(false);
      handleFeedback('success', type === 'new' ? 'NewClinician' : 'UpdateClinician');
    } else if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
      handleFeedback('error', 'TimeOut');
    } else if (response === 400) { // Ja hi ha un usuari amb aquest nom
      handleFeedback('error', type === 'new' ? 'NewClinicianAlreadyExists' : 'UpdateClinicianAlreadyExists');
    } else { // Error amb les dades o al servidor
      handleFeedback('error', type === 'new' ? 'NewClinician' : 'UpdateClinician');
    }
  };

  return (
    <Formik
      initialValues={type === 'new' ? {
        _id: '',
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
      } : {
        _id: detailsData ? detailsData._id : '', // Per evitar un error quan es fa un reload de la pestanya del navegador, comprovam que detailsData existeix abans d'intentar agafar-ne cap valor. Si no exiteix posam '' dins la propietat per evitar warnings
        username: detailsData ? detailsData.username : '', // Idem
        firstName: detailsData ? detailsData.firstName : '', // Idem
        lastName: detailsData ? detailsData.lastName : '', // Idem
        email: detailsData ? detailsData.email : '', // Idem
        password: '',
        confirmPassword: '',
      }}
      validationSchema={type === 'new' ? newClinicianDetailsVS : updateClinicianDetailsVS }
      onSubmit={(values, { resetForm }) => { updateData(values, resetForm); }}
    >
      {({ values, touched, errors, handleChange, handleBlur, submitForm }) => (
        <React.Fragment>
          <SpinnerLoading show={showLoading} />
          <Grid item xs={6}>
            <Grid container direction='column' spacing={3}>
              <Grid item>
                <TextField
                  disabled
                  fullWidth
                  label={translations['_idUser']}
                  variant='outlined'
                  id='idUser'
                  name='idUser'
                  value={values._id}
                />
              </Grid>
              <Grid item>
                <TextField
                  disabled={type === 'edit' || false}
                  required
                  fullWidth
                  label={translations['_username']}
                  variant='outlined'
                  id='username'
                  name='username'
                  error={Boolean(touched.username && errors.username)}
                  value={values.username.replace(/\s/g, '')} // Per evitar que es puguin introduir espais en blanc
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.username && errors.username ? errors.username : ''}
                />
              </Grid>
              <Grid item>
                <TextField
                  fullWidth
                  label={translations['_firstName']}
                  variant='outlined'
                  id='firstName'
                  name='firstName'
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item>
                <TextField
                  fullWidth
                  label={translations['_lastName']}
                  variant='outlined'
                  id='lastName'
                  name='lastName'
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item>
                <TextField
                  fullWidth
                  label={translations['_email']}
                  variant='outlined'
                  id='email'
                  name='email'
                  error={Boolean(touched.email && errors.email)}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.email && errors.email ? errors.email : ''}
                />
              </Grid>
              {type === 'new' ? (
                <React.Fragment>
                  <Grid item>
                    <TextField
                      required
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      variant='outlined'
                      id='password'
                      name='password'
                      label={translations['_password']}
                      error={Boolean(touched.password && errors.password)}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.password && errors.password ? errors.password : ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <IconButton
                              onClick={onChangePasswordVisibility}
                              onMouseDown={onMouseDownPassword}
                              edge='end'
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      required
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      variant='outlined'
                      id='confirmPassword'
                      name='confirmPassword'
                      label={translations['_confirmPassword']}
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <IconButton
                              onClick={onChangeConfirmPasswordVisibility}
                              onMouseDown={onMouseDownPassword}
                              edge='end'
                            >
                              {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Grid item>
                    <TextField
                      disabled
                      // required
                      fullWidth
                      type='password'
                      variant='outlined'
                      id='changePassword'
                      name='changePassword'
                      label={translations['_password']}
                      value='000000' // Cadena per a que es mostrin 6 bullets al camp de password
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <IconButton
                              onClick={handleShowChangePassword}
                              onMouseDown={onMouseDownPassword}
                              edge='end'
                            >
                              <VpnKey />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </React.Fragment>
              )}

            </Grid>
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <Grid container spacing={2} direction='row' justify='flex-end'>
              <Grid item>
                {sessionContext.role === 'Admin' && <BasicButton handleClick={submitForm} type='accept' />}
              </Grid>
              <Grid item>
                <BasicButton handleClick={goBack} type='cancel' />
              </Grid>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </Formik>
  );
}

// Indica i controla els props que se han de passar al component
ClinicianDetails.propTypes = {
  detailsData: PropTypes.object.isRequired,
  handleFeedback: PropTypes.func.isRequired,
  handleShowChangePassword: PropTypes.func,
  type: PropTypes.string.isRequired,
};
